import { readApiCompatibility } from './api/client.js'
import { createContext, useContext, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { AppUpdateNotice, buildIdentity, readAppVersion, useAppUpdate } from '@speaking-coach/ui'
import { appRefreshSafety, appCompatibility } from './app-update-safety.js'
import { useAtomValue, useStore } from 'jotai'
import { adminUserAtom } from './state/atoms.js'

export const AdminUpdateContext = createContext<ReturnType<typeof useAppUpdate> | null>(null)
export function AdminUpdateProvider({ children }: { children: ReactNode }) {
  if (typeof __APP_BUILD__ === 'undefined' || !buildIdentity(__APP_BUILD__)) return <>{children}</>
  return <AdminLiveUpdateProvider>{children}</AdminLiveUpdateProvider>
}
function AdminLiveUpdateProvider({ children }: { children: ReactNode }) {
  const user = useAtomValue(adminUserAtom)
  const store = useStore()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  useIsMutating() // Subscribe; activation below reads the cache synchronously again.
  const update = useAppUpdate({
    running: typeof __APP_BUILD__ === 'undefined' ? null : __APP_BUILD__,
    account: user?.role === 'admin' ? `${user.clientId}:${user.id}` : null,
    load: readAppVersion,
    compatibility: { state: appCompatibility, load: readApiCompatibility },
    safety: appRefreshSafety,
    storageKey: 'admin:app-update',
    canRefresh: () => pathname.startsWith('/admin/') && store.get(adminUserAtom)?.id === user?.id && store.get(adminUserAtom)?.clientId === user?.clientId && store.get(adminUserAtom)?.role === 'admin' && queryClient.isMutating() === 0,
  })
  return <AdminUpdateContext.Provider value={update}>{children}</AdminUpdateContext.Provider>
}
export function AdminUpdatePrompt() {
  const update = useContext(AdminUpdateContext)
  const user = useAtomValue(adminUserAtom)
  return <AppUpdateNotice update={update} isInternal={user?.isInternal} copy={{
    required: { title: "An update is required", ready: "Refresh the app before starting new work.", busy: "Your page is still open. Copy any unsaved changes before closing the form, and wait for work in progress to finish.", offline: "Reconnect to the internet to check for the update. Your page will stay open.", waiting: "The update is not ready to load. Keep this page open and check again shortly. If this continues, contact support.", checkLabel: "Check again" },
    message: 'A new version is available', refreshLabel: 'Refresh', laterLabel: 'Later',
    laterDescription: 'Remind me in 30 min', currentLabel: 'Current', availableLabel: 'Available',
  }} />
}
